import type { TFunction } from 'i18next';

import type { Opportunity } from '../components/OpportunityPageTypes';
import { initialOpportunityForm } from '../components/OpportunityPageTypes';
import type { Options } from './types';
import { readRowCell } from './excelImportHelpers';

export type OpportunityImportHeaders = {
  owner: string;
  client: string;
  contact: string;
  name: string;
  remito: string;
  value: string;
  discount: string;
  stage: string;
};

export function buildOpportunityImportHeaders(t: TFunction): OpportunityImportHeaders {
  return {
    owner: t('opportunity.table.owner'),
    client: t('opportunity.table.client'),
    contact: t('opportunity.table.contact'),
    name: t('opportunity.table.opportunity'),
    remito: t('opportunity.table.remito'),
    value: t('opportunity.table.value'),
    discount: t('opportunity.table.discount'),
    stage: t('opportunity.table.stage'),
  };
}

function findOptionIdByLabel(options: Options[], label: string): string | null {
  const normalized = label.trim().toLowerCase();
  if (!normalized) {
    return null;
  }
  const match = options.find((option) => option.label.trim().toLowerCase() === normalized);
  return match?.value ?? null;
}

function parseNumberCell(value: unknown): number | undefined {
  if (value == null || String(value).trim() === '') {
    return undefined;
  }
  const parsed = Number.parseFloat(String(value).replace(/[^\d.-]/g, ''));
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function applyOpportunityImportRow(
  base: Opportunity,
  row: Record<string, unknown>,
  headers: OpportunityImportHeaders,
  ownerOptions: Options[],
  contactOptions: Options[],
  clientOptions: Options[],
): Opportunity {
  const patched: Opportunity = { ...base };

  const name = readRowCell(row, headers.name);
  if (name !== undefined) {
    patched.name = String(name);
  }

  const remito = readRowCell(row, headers.remito);
  if (remito !== undefined) {
    patched.remito = String(remito);
  }

  const value = parseNumberCell(readRowCell(row, headers.value));
  if (value !== undefined) {
    patched.value = value;
  }

  const discount = parseNumberCell(readRowCell(row, headers.discount));
  if (discount !== undefined) {
    patched.discount = discount;
  }

  const ownerLabel = readRowCell(row, headers.owner);
  if (ownerLabel !== undefined) {
    const ownerId = findOptionIdByLabel(ownerOptions, String(ownerLabel));
    patched.owner = ownerId;
  }

  const contactLabel = readRowCell(row, headers.contact);
  if (contactLabel !== undefined) {
    const contactId = findOptionIdByLabel(contactOptions, String(contactLabel));
    if (contactId) {
      patched.contact_id = contactId;
    }
  }

  const clientLabel = readRowCell(row, headers.client);
  if (clientLabel !== undefined) {
    const clientId = findOptionIdByLabel(clientOptions, String(clientLabel));
    patched.client = String(clientLabel);
    patched.client_ids = clientId ? [clientId] : [];
  }

  return patched;
}

export function buildOpportunityFromImportRow(
  row: Record<string, unknown>,
  headers: OpportunityImportHeaders,
  ownerOptions: Options[],
  contactOptions: Options[],
  clientOptions: Options[],
  defaultUserId: string,
): { opportunity: Opportunity | null; errors: string[] } {
  const draft: Opportunity = {
    ...initialOpportunityForm,
    id: '',
    user_id: defaultUserId,
    owner: null,
    products: [],
    files: [],
    stages: initialOpportunityForm.stages.map((stage) => ({
      ...stage,
      opportunity_id: '',
    })),
    startDate: new Date().toISOString(),
    deletedAt: null,
  };

  const merged = applyOpportunityImportRow(
    draft,
    row,
    headers,
    ownerOptions,
    contactOptions,
    clientOptions,
  );

  const errors: string[] = [];
  if (!merged.name.trim()) {
    errors.push('name');
  }
  if (merged.client_ids.length === 0) {
    errors.push('client');
  }
  if (!merged.contact_id) {
    errors.push('contact');
  }

  if (errors.length > 0) {
    return { opportunity: null, errors };
  }

  return { opportunity: merged, errors: [] };
}

export function formatOpportunityImportValidationError(
  t: TFunction,
  line: number,
  missing: string[],
): string {
  const labels: string[] = [];
  if (missing.includes('name')) {
    labels.push(t('opportunity.table.opportunity'));
  }
  if (missing.includes('client')) {
    labels.push(t('opportunity.table.client'));
  }
  if (missing.includes('contact')) {
    labels.push(t('opportunity.table.contact'));
  }
  return t('common.import.rowError', {
    line,
    errors: labels.join(', '),
  });
}
